import React, { useState, useEffect } from 'react';
import './Innstillinger.css';

const crmApi = {
  baseUrl: process.env.REACT_APP_API_URL || 'https://advokat-crm-api.stig-958.workers.dev/api'
};

const Innstillinger = () => {
  const [innstillinger, setInnstillinger] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const [formData, setFormData] = useState({
    kontornavn: '',
    organisasjonsnummer: '',
    telefon: '',
    epost: '',
    nettside: '',
    postadresse: '',
    postnummer: '',
    poststed: '',
    kontonummer: '',
    mvapliktig: true,
    mva_prosent: 25.0,
    standard_timesats: 1575.0,
    betalingsbetingelser_dager: 14
  });

  useEffect(() => {
    hentInnstillinger();
  }, []);

  const hentInnstillinger = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');

      const response = await fetch(`${crmApi.baseUrl}/innstillinger`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Kunne ikke hente innstillinger');
      }

      const result = await response.json();
      setInnstillinger(result.data);
      setFormData({
        kontornavn: result.data.kontornavn || '',
        organisasjonsnummer: result.data.organisasjonsnummer || '',
        telefon: result.data.telefon || '',
        epost: result.data.epost || '',
        nettside: result.data.nettside || '',
        postadresse: result.data.postadresse || '',
        postnummer: result.data.postnummer || '',
        poststed: result.data.poststed || '',
        kontonummer: result.data.kontonummer || '',
        mvapliktig: result.data.mvapliktig !== 0,
        mva_prosent: result.data.mva_prosent || 25.0,
        standard_timesats: result.data.standard_timesats || 1575.0,
        betalingsbetingelser_dager: result.data.betalingsbetingelser_dager || 14
      });
    } catch (error) {
      console.error('Feil ved henting av innstillinger:', error);
      setErrorMessage('Kunne ikke hente innstillinger');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);
      setErrorMessage('');
      setSuccessMessage('');

      const token = localStorage.getItem('access_token');

      // Konverter numeriske verdier
      const payload = {
        ...formData,
        mva_prosent: parseFloat(formData.mva_prosent),
        standard_timesats: parseFloat(formData.standard_timesats),
        betalingsbetingelser_dager: parseInt(formData.betalingsbetingelser_dager)
      };

      const response = await fetch(`${crmApi.baseUrl}/innstillinger`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Kunne ikke lagre innstillinger');
      }

      const result = await response.json();
      setInnstillinger(result.data);
      setSuccessMessage('✅ Innstillinger lagret!');

      // Fjern suksessmelding etter 3 sekunder
      setTimeout(() => setSuccessMessage(''), 3000);

    } catch (error) {
      console.error('Feil ved lagring:', error);
      setErrorMessage(`❌ ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="innstillinger-container">
        <div className="loading">Laster innstillinger...</div>
      </div>
    );
  }

  return (
    <div className="innstillinger-container">
      <div className="innstillinger-header">
        <h1>⚙️ Innstillinger</h1>
        <p className="subtitle">
          Disse innstillingene brukes i oppdragsbekreftelser, fakturaer og andre dokumenter
        </p>
      </div>

      {successMessage && (
        <div className="alert alert-success">{successMessage}</div>
      )}

      {errorMessage && (
        <div className="alert alert-error">{errorMessage}</div>
      )}

      <form onSubmit={handleSubmit} className="innstillinger-form">

        {/* Kontorinfo */}
        <div className="form-section">
          <h2>🏢 Kontorinfo</h2>

          <div className="form-group">
            <label htmlFor="kontornavn">Kontornavn *</label>
            <input
              type="text"
              id="kontornavn"
              name="kontornavn"
              value={formData.kontornavn}
              onChange={handleChange}
              required
              placeholder="Nesodden Advokatkontor AS"
            />
          </div>

          <div className="form-group">
            <label htmlFor="organisasjonsnummer">Organisasjonsnummer</label>
            <input
              type="text"
              id="organisasjonsnummer"
              name="organisasjonsnummer"
              value={formData.organisasjonsnummer}
              onChange={handleChange}
              placeholder="936789064"
              maxLength="9"
            />
            <small>9 siffer</small>
          </div>
        </div>

        {/* Kontaktinfo */}
        <div className="form-section">
          <h2>📞 Kontaktinfo</h2>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="telefon">Telefon</label>
              <input
                type="tel"
                id="telefon"
                name="telefon"
                value={formData.telefon}
                onChange={handleChange}
                placeholder="+47 123 45 678"
              />
            </div>

            <div className="form-group">
              <label htmlFor="epost">E-post *</label>
              <input
                type="email"
                id="epost"
                name="epost"
                value={formData.epost}
                onChange={handleChange}
                required
                placeholder="post@advokatkontor.no"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="nettside">Nettside</label>
            <input
              type="url"
              id="nettside"
              name="nettside"
              value={formData.nettside}
              onChange={handleChange}
              placeholder="www.advokatkontor.no"
            />
          </div>
        </div>

        {/* Postadresse */}
        <div className="form-section">
          <h2>📍 Postadresse</h2>

          <div className="form-group">
            <label htmlFor="postadresse">Gateadresse</label>
            <input
              type="text"
              id="postadresse"
              name="postadresse"
              value={formData.postadresse}
              onChange={handleChange}
              placeholder="Bommenveien 9"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="postnummer">Postnummer</label>
              <input
                type="text"
                id="postnummer"
                name="postnummer"
                value={formData.postnummer}
                onChange={handleChange}
                placeholder="1454"
                maxLength="4"
              />
            </div>

            <div className="form-group">
              <label htmlFor="poststed">Poststed</label>
              <input
                type="text"
                id="poststed"
                name="poststed"
                value={formData.poststed}
                onChange={handleChange}
                placeholder="Fagerstrand"
              />
            </div>
          </div>
        </div>

        {/* Bankinfo */}
        <div className="form-section">
          <h2>🏦 Bankinfo</h2>

          <div className="form-group">
            <label htmlFor="kontonummer">Kontonummer</label>
            <input
              type="text"
              id="kontonummer"
              name="kontonummer"
              value={formData.kontonummer}
              onChange={handleChange}
              placeholder="1234.56.78901"
              maxLength="13"
            />
            <small>Format: 1234.56.78901</small>
          </div>
        </div>

        {/* Juridisk/Økonomi */}
        <div className="form-section">
          <h2>⚖️ Juridisk og Økonomi</h2>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                name="mvapliktig"
                checked={formData.mvapliktig}
                onChange={handleChange}
              />
              <span>MVA-pliktig</span>
            </label>
          </div>

          {formData.mvapliktig && (
            <div className="form-group">
              <label htmlFor="mva_prosent">MVA-prosent</label>
              <input
                type="number"
                id="mva_prosent"
                name="mva_prosent"
                value={formData.mva_prosent}
                onChange={handleChange}
                min="0"
                max="100"
                step="0.1"
              />
              <small>Standard er 25%</small>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="standard_timesats">Standard timesats (ekskl. MVA)</label>
            <input
              type="number"
              id="standard_timesats"
              name="standard_timesats"
              value={formData.standard_timesats}
              onChange={handleChange}
              min="0"
              step="0.01"
            />
            <small>NOK per time (eks. 1575)</small>
          </div>

          <div className="form-group">
            <label htmlFor="betalingsbetingelser_dager">Betalingsfrist (dager)</label>
            <input
              type="number"
              id="betalingsbetingelser_dager"
              name="betalingsbetingelser_dager"
              value={formData.betalingsbetingelser_dager}
              onChange={handleChange}
              min="1"
              max="90"
            />
            <small>Antall dager fra fakturadato (standard: 14 dager)</small>
          </div>
        </div>

        {/* Lagre-knapp */}
        <div className="form-actions">
          <button
            type="submit"
            className="btn-primary btn-large"
            disabled={saving}
          >
            {saving ? '💾 Lagrer...' : '💾 Lagre innstillinger'}
          </button>
        </div>
      </form>

      {/* Forhåndsvisning */}
      {innstillinger && (
        <div className="preview-section">
          <h2>👁️ Forhåndsvisning</h2>
          <div className="preview-box">
            <h3>{formData.kontornavn}</h3>
            {formData.postadresse && (
              <p>{formData.postadresse}, {formData.postnummer} {formData.poststed}</p>
            )}
            {formData.telefon && <p>Tlf: {formData.telefon}</p>}
            {formData.epost && <p>E-post: {formData.epost}</p>}
            {formData.nettside && <p>Web: {formData.nettside}</p>}
            {formData.organisasjonsnummer && (
              <p style={{marginTop: '10px', fontSize: '0.9em', color: '#666'}}>
                Org.nr: {formData.organisasjonsnummer}
              </p>
            )}
          </div>
          <p className="preview-note">
            Slik vil kontorinfo vises i oppdragsbekreftelser og andre dokumenter
          </p>
        </div>
      )}
    </div>
  );
};

export default Innstillinger;
